const haackCache = {}
const ellipseCache = {}

export function approximateHaack(diameter, noseLength, meplat, c = 0) {
  if (!diameter || !noseLength) return { rtr: .5, meplat: 0 }
  const aspectRatio = Math.round((noseLength / diameter) * 20) / 20
  const key = `${aspectRatio.toFixed(2)}:${c.toFixed(2)}:${meplat.toFixed(3)}`
  if (haackCache[key]) return {
    rtr: haackCache[key].rtr,
    meplat: haackCache[key].meplat * diameter
  }
  let minError = Math.min()
  let bestRtr = 0
  let bestMeplat = 0
  for (let meplatFit = 0; meplatFit < diameter / 3; meplatFit += .001) {
    for (let rtr = 0; rtr <= 1; rtr += .01) {
      let error = 0
      for (let x = 0; x <= noseLength; x += .01) {
        const { radiusInches, centerX, centerY } = calculateCircle(diameter, noseLength, rtr, meplatFit)
        const sy = Math.sqrt(radiusInches * radiusInches - (x - centerX) * (x - centerX)) + centerY
        const hy = haack(diameter - meplat, noseLength, noseLength - x, c) + meplat / 2
        error += Math.abs(sy - hy) ** 2
      }
      if (error < minError) {
        minError = error
        bestRtr = rtr
        bestMeplat = meplatFit
      }
    }
  }
  const best = { rtr: bestRtr, meplat: bestMeplat }
  haackCache[key] = { rtr: bestRtr, meplat: bestMeplat / diameter }
  return best
}

export function approximateEllipse (diameter, noseLength) {
  if (!diameter || !noseLength) return { rtr: 5, meplat: diameter / 2 }
  const key = `${diameter.toFixed(3)}:${noseLength.toFixed(3)}`
  if (ellipseCache[key]) return ellipseCache[key]
  let minError = Math.min()
  let bestRtr = 0
  let bestMeplat = 0
  for (let meplat = 0; meplat < diameter; meplat += .01) {
    for (let rtr = 0; rtr <= 1; rtr += .01) {
      let error = 0
      for (let x = 0; x <= noseLength; x += .01) {
        const { radiusInches, centerX, centerY } = calculateCircle(diameter, noseLength, rtr, meplat)
        const sy = Math.sqrt(radiusInches * radiusInches - (x - centerX) * (x - centerX)) + centerY
        //const ey  = (diameter / 2) * Math.sqrt(1 - (Math.pow(x, 2) / Math.pow(noseLength, 2)))
        const ey = ellipseNose(diameter, noseLength, noseLength - x)
        error += Math.abs(sy - ey) ** 2
      }
      if (error < minError) {
        minError = error
        bestRtr = rtr
        bestMeplat = meplat
      }
    }
  }
  const best = { rtr: bestRtr, meplat: bestMeplat }
  ellipseCache[key] = best
  return best
}

export function approximatePower (diameter, noseLength, p) {
  const rtr = parseFloat((-1.15381 * p + 1.14839).toFixed(2))
  const meplat = parseFloat(((-.36127 * p + .33856) * diameter).toFixed(3))
  return { rtr, meplat }
}

export function ellipseNose (d, l, x) {
  return (d / 2) * Math.sqrt(1 - (Math.pow(x, 2) / Math.pow(l, 2)))
}

export function haack(d, l, x, C = 0) {
  const theta = Math.acos(1 - 2 * x / l);
  return (d / 2) * Math.sqrt((theta - Math.sin(theta) * Math.cos(theta) + C * Math.pow(Math.sin(theta), 3)) / Math.PI) || 0;
}

//export function haack (d, l, x) {
//  return ((d / 4) * Math.sqrt(
//    (
//      (4 * Math.PI)
//      - (4 * Math.acos((-l + 2 * x) / l))
//      + (2 * Math.sin(2 * Math.acos((-l + 2 * x) / l)))
//    )
//    / Math.PI
//  )) || 0
//}

export function powerNose (d, l, x, p = .75) {
  return (Math.pow(Math.max(x, 0), p) / Math.pow(l, p)) * (d / 2)
}

function calculateCircle(diameter, noseLength, rtr, meplat) {
  if (rtr === 0) rtr = 0.001
  let bulletRadius = diameter / 2
  let meplatRadius = meplat / 2
  let rise = meplatRadius - bulletRadius
  let run = noseLength
  let m = -run/rise
  let pmx = noseLength / 2
  let pmy = ((bulletRadius - meplatRadius) / 2) + meplatRadius
  let b = pmy - m * pmx
  let tangentRadius = bulletRadius - b
  let radius = tangentRadius / rtr

  // http://mathforum.org/library/drmath/view/53027.html
  let x1 = 0
  let y1 = 0
  let x2 = noseLength
  let y2 = meplatRadius - bulletRadius
  let x3 = x2 / 2
  let y3 = y2 / 2
  let r = radius
  let q = Math.sqrt(Math.pow(x2-x1, 2) + Math.pow(y2-y1, 2))
  let cx = x3 - Math.sqrt(r*r-Math.pow(q/2, 2))*(y1-y2)/q
  let cy = y3 - Math.sqrt(r*r-Math.pow(q/2, 2))*(x2-x1)/q + bulletRadius

  return {
    centerX: cx,
    centerY: cy,
    radiusInches: radius
  }
}

// nose length, rtr, meplat
const haackCconversions = {
  "0.177": [
    [ 0.177, 0.69, 0.01 ],
    [ 0.18, 0.68, 0.011 ],
    [ 0.182, 0.69, 0.01 ],
    [ 0.189, 0.69, 0.011 ],
    [ 0.192, 0.7, 0.01 ],
    [ 0.198, 0.69, 0.011 ],
    [ 0.201, 0.71, 0.009 ],
    [ 0.203, 0.7, 0.01 ],
    [ 0.206, 0.7, 0.011 ],
    [ 0.216, 0.7, 0.011 ],
    [ 0.22, 0.7, 0.012 ],
    [ 0.222, 0.71, 0.011 ],
    [ 0.226, 0.7, 0.012 ],
    [ 0.231, 0.71, 0.011 ],
    [ 0.236, 0.7, 0.012 ],
    [ 0.238, 0.71, 0.012 ],
    [ 0.242, 0.72, 0.011 ],
    [ 0.244, 0.71, 0.012 ],
    [ 0.251, 0.72, 0.011 ],
    [ 0.254, 0.71, 0.012 ],
    [ 0.261, 0.72, 0.011 ],
    [ 0.264, 0.71, 0.012 ],
    [ 0.269, 0.72, 0.012 ],
    [ 0.271, 0.72, 0.011 ],
    [ 0.273, 0.72, 0.012 ],
    [ 0.309, 0.72, 0.013 ],
    [ 0.311, 0.73, 0.012 ],
    [ 0.315, 0.72, 0.013 ],
    [ 0.321, 0.73, 0.012 ],
    [ 0.325, 0.72, 0.013 ],
    [ 0.331, 0.73, 0.012 ],
    [ 0.334, 0.72, 0.013 ],
    [ 0.341, 0.73, 0.012 ],
    [ 0.344, 0.72, 0.013 ],
    [ 0.351, 0.73, 0.012 ],
    [ 0.355, 0.72, 0.013 ],
    [ 0.361, 0.72, 0.013 ],
    [ 0.371, 0.72, 0.013 ],
    [ 0.374, 0.73, 0.013 ],
    [ 0.509, 0.73, 0.014 ],
    [ 0.511, 0.73, 0.013 ],
    [ 0.518, 0.73, 0.014 ],
    [ 0.521, 0.73, 0.013 ],
    [ 0.527, 0.73, 0.014 ],
    [ 0.531, 0.74, 0.013 ],
    [ 0.535, 0.73, 0.014 ],
    [ 0.541, 0.74, 0.013 ],
    [ 0.545, 0.73, 0.014 ],
    [ 0.551, 0.74, 0.013 ],
    [ 0.555, 0.73, 0.014 ],
    [ 0.561, 0.74, 0.013 ],
    [ 0.565, 0.73, 0.014 ],
    [ 0.571, 0.74, 0.013 ],
    [ 0.575, 0.73, 0.014 ],
    [ 0.581, 0.74, 0.013 ],
    [ 0.585, 0.73, 0.014 ],
    [ 0.591, 0.74, 0.013 ],
    [ 0.595, 0.73, 0.014 ],
    [ 0.601, 0.74, 0.013 ],
    [ 0.604, 0.73, 0.014 ],
    [ 0.611, 0.74, 0.013 ],
    [ 0.613, 0.73, 0.014 ],
    [ 0.621, 0.73, 0.014 ],
    [ 0.631, 0.74, 0.013 ],
    [ 0.633, 0.73, 0.014 ],
    [ 0.641, 0.74, 0.013 ],
    [ 0.643, 0.73, 0.014 ],
    [ 0.651, 0.74, 0.013 ],
    [ 0.653, 0.73, 0.014 ],
    [ 0.661, 0.74, 0.013 ],
    [ 0.663, 0.73, 0.014 ],
    [ 0.671, 0.74, 0.013 ],
    [ 0.673, 0.73, 0.014 ],
    [ 0.681, 0.74, 0.013 ],
    [ 0.683, 0.73, 0.014 ],
    [ 0.691, 0.74, 0.013 ],
    [ 0.693, 0.73, 0.014 ],
    [ 0.721, 0.73, 0.014 ],
    [ 0.751, 0.73, 0.014 ],
    [ 0.761, 0.73, 0.014 ],
    [ 0.771, 0.73, 0.014 ],
    [ 1.206, 0.74, 0.014 ],
    [ 1.208, 0.73, 0.014 ],
    [ 1.21, 0.74, 0.014 ]
  ],
  "0.204": [
    [ 0.205, 0.69, 0.011 ],
    [ 0.209, 0.69, 0.012 ],
    [ 0.212, 0.7, 0.01 ],
    [ 0.214, 0.7, 0.011 ],
    [ 0.217, 0.69, 0.012 ],
    [ 0.222, 0.7, 0.011 ],
    [ 0.226, 0.69, 0.013 ],
    [ 0.228, 0.7, 0.012 ],
    [ 0.231, 0.7, 0.012 ],
    [ 0.24, 0.7, 0.013 ],
    [ 0.242, 0.7, 0.013 ],
    [ 0.252, 0.7, 0.013 ],
    [ 0.259, 0.7, 0.014 ],
    [ 0.262, 0.71, 0.013 ],
    [ 0.269, 0.7, 0.014 ],
    [ 0.272, 0.71, 0.013 ],
    [ 0.28, 0.71, 0.014 ],
    [ 0.282, 0.71, 0.013 ],
    [ 0.287, 0.71, 0.014 ],
    [ 0.302, 0.71, 0.014 ],
    [ 0.307, 0.71, 0.014 ],
    [ 0.312, 0.72, 0.013 ],
    [ 0.316, 0.72, 0.014 ],
    [ 0.32, 0.71, 0.015 ],
    [ 0.322, 0.72, 0.014 ],
    [ 0.331, 0.72, 0.014 ],
    [ 0.341, 0.72, 0.014 ],
    [ 0.359, 0.72, 0.015 ],
    [ 0.362, 0.72, 0.014 ],
    [ 0.366, 0.72, 0.015 ],
    [ 0.372, 0.72, 0.015 ],
    [ 0.382, 0.72, 0.015 ],
    [ 0.392, 0.73, 0.014 ],
    [ 0.394, 0.72, 0.015 ],
    [ 0.402, 0.72, 0.015 ],
    [ 0.412, 0.73, 0.014 ],
    [ 0.414, 0.72, 0.015 ],
    [ 0.422, 0.73, 0.014 ],
    [ 0.425, 0.72, 0.015 ],
    [ 0.431, 0.73, 0.014 ],
    [ 0.434, 0.73, 0.015 ],
    [ 0.578, 0.73, 0.016 ],
    [ 0.581, 0.73, 0.015 ],
    [ 0.586, 0.73, 0.016 ],
    [ 0.591, 0.73, 0.015 ],
    [ 0.595, 0.73, 0.016 ],
    [ 0.601, 0.73, 0.015 ],
    [ 0.603, 0.73, 0.016 ],
    [ 0.621, 0.73, 0.016 ],
    [ 0.631, 0.73, 0.016 ],
    [ 0.641, 0.73, 0.016 ],
    [ 0.651, 0.73, 0.016 ],
    [ 0.661, 0.73, 0.016 ],
    [ 0.751, 0.73, 0.016 ],
    [ 0.761, 0.73, 0.016 ],
    [ 0.771, 0.73, 0.016 ],
    [ 0.851, 0.73, 0.016 ],
    [ 0.861, 0.73, 0.016 ],
    [ 0.871, 0.73, 0.016 ],
    [ 0.951, 0.73, 0.016 ],
    [ 0.961, 0.73, 0.016 ],
    [ 0.971, 0.73, 0.016 ],
    [ 0.981, 0.73, 0.016 ],
    [ 1.131, 0.74, 0.016 ],
    [ 1.137, 0.74, 0.016 ]
  ],
  "0.223": [
    [ 0.224, 0.69, 0.012 ],
    [ 0.229, 0.69, 0.013 ],
    [ 0.242, 0.7, 0.012 ],
    [ 0.245, 0.69, 0.014 ],
    [ 0.25, 0.7, 0.013 ],
    [ 0.257, 0.7, 0.014 ],
    [ 0.262, 0.7, 0.014 ],
    [ 0.272, 0.71, 0.013 ],
    [ 0.274, 0.7, 0.014 ],
    [ 0.278, 0.7, 0.015 ],
    [ 0.282, 0.71, 0.014 ],
    [ 0.289, 0.7, 0.015 ],
    [ 0.292, 0.71, 0.014 ],
    [ 0.3, 0.71, 0.015 ],
    [ 0.302, 0.71, 0.014 ],
    [ 0.304, 0.71, 0.015 ],
    [ 0.33, 0.71, 0.016 ],
    [ 0.332, 0.71, 0.015 ],
    [ 0.335, 0.72, 0.015 ],
    [ 0.338, 0.71, 0.016 ],
    [ 0.342, 0.72, 0.015 ],
    [ 0.348, 0.71, 0.016 ],
    [ 0.352, 0.72, 0.015 ],
    [ 0.36, 0.71, 0.016 ],
    [ 0.362, 0.72, 0.015 ],
    [ 0.371, 0.72, 0.015 ],
    [ 0.378, 0.72, 0.016 ],
    [ 0.382, 0.72, 0.016 ],
    [ 0.432, 0.72, 0.016 ],
    [ 0.439, 0.72, 0.017 ],
    [ 0.442, 0.73, 0.015 ],
    [ 0.444, 0.73, 0.016 ],
    [ 0.447, 0.72, 0.017 ],
    [ 0.452, 0.73, 0.016 ],
    [ 0.456, 0.72, 0.017 ],
    [ 0.462, 0.73, 0.016 ],
    [ 0.468, 0.72, 0.017 ],
    [ 0.472, 0.73, 0.016 ],
    [ 0.478, 0.72, 0.017 ],
    [ 0.482, 0.73, 0.016 ],
    [ 0.489, 0.72, 0.017 ],
    [ 0.492, 0.73, 0.016 ],
    [ 0.5, 0.73, 0.016 ],
    [ 0.539, 0.73, 0.017 ],
    [ 0.541, 0.73, 0.016 ],
    [ 0.548, 0.73, 0.017 ],
    [ 0.551, 0.73, 0.016 ],
    [ 0.556, 0.73, 0.017 ],
    [ 0.561, 0.73, 0.016 ],
    [ 0.563, 0.73, 0.017 ],
    [ 0.82, 0.73, 0.017 ],
    [ 0.829, 0.73, 0.018 ],
    [ 0.831, 0.73, 0.017 ],
    [ 0.838, 0.73, 0.018 ],
    [ 0.841, 0.73, 0.017 ],
    [ 0.847, 0.73, 0.018 ],
    [ 0.851, 0.74, 0.017 ],
    [ 0.856, 0.73, 0.018 ],
    [ 0.861, 0.74, 0.017 ],
    [ 0.867, 0.73, 0.018 ],
    [ 0.871, 0.74, 0.017 ],
    [ 0.877, 0.73, 0.018 ],
    [ 0.881, 0.74, 0.017 ],
    [ 0.887, 0.73, 0.018 ],
    [ 0.891, 0.74, 0.017 ],
    [ 0.897, 0.73, 0.018 ],
    [ 0.901, 0.74, 0.017 ],
    [ 0.906, 0.73, 0.018 ],
    [ 0.911, 0.74, 0.017 ],
    [ 0.916, 0.73, 0.018 ],
    [ 0.921, 0.74, 0.017 ],
    [ 0.927, 0.73, 0.018 ],
    [ 0.931, 0.74, 0.017 ],
    [ 0.937, 0.73, 0.018 ],
    [ 0.941, 0.74, 0.017 ],
    [ 0.947, 0.73, 0.018 ],
    [ 0.951, 0.74, 0.017 ],
    [ 0.957, 0.73, 0.018 ],
    [ 0.961, 0.74, 0.017 ],
    [ 0.967, 0.73, 0.018 ],
    [ 0.971, 0.74, 0.017 ],
    [ 0.977, 0.73, 0.018 ],
    [ 0.981, 0.74, 0.017 ],
    [ 0.987, 0.73, 0.018 ],
    [ 0.991, 0.74, 0.017 ],
    [ 0.998, 0.73, 0.018 ],
    [ 1.001, 0.74, 0.017 ],
    [ 1.007, 0.73, 0.018 ],
    [ 1.012, 0.74, 0.017 ],
    [ 1.017, 0.73, 0.018 ],
    [ 1.022, 0.74, 0.017 ],
    [ 1.028, 0.73, 0.018 ],
    [ 1.032, 0.74, 0.017 ],
    [ 1.038, 0.73, 0.018 ],
    [ 1.042, 0.74, 0.017 ],
    [ 1.048, 0.73, 0.018 ],
    [ 1.052, 0.74, 0.017 ],
    [ 1.058, 0.73, 0.018 ],
    [ 1.062, 0.74, 0.017 ],
    [ 1.068, 0.73, 0.018 ],
    [ 1.072, 0.74, 0.017 ],
    [ 1.078, 0.73, 0.018 ],
    [ 1.082, 0.74, 0.017 ],
    [ 1.088, 0.73, 0.018 ],
    [ 1.092, 0.74, 0.017 ],
    [ 1.099, 0.73, 0.018 ],
    [ 1.102, 0.74, 0.017 ],
    [ 1.109, 0.73, 0.018 ],
    [ 1.112, 0.74, 0.017 ],
    [ 1.119, 0.73, 0.018 ],
    [ 1.122, 0.74, 0.017 ],
    [ 1.129, 0.73, 0.018 ],
    [ 1.132, 0.74, 0.017 ],
    [ 1.139, 0.73, 0.018 ],
    [ 1.142, 0.74, 0.017 ],
    [ 1.149, 0.73, 0.018 ],
    [ 1.152, 0.74, 0.017 ],
    [ 1.159, 0.73, 0.018 ],
    [ 1.162, 0.74, 0.017 ],
    [ 1.169, 0.73, 0.018 ],
    [ 1.172, 0.74, 0.017 ],
    [ 1.179, 0.73, 0.018 ],
    [ 1.182, 0.74, 0.017 ],
    [ 1.189, 0.73, 0.018 ],
    [ 1.192, 0.74, 0.017 ],
    [ 1.2, 0.73, 0.018 ],
    [ 1.202, 0.74, 0.017 ],
    [ 1.21, 0.73, 0.018 ],
    [ 1.212, 0.74, 0.017 ],
    [ 1.22, 0.73, 0.018 ],
    [ 1.222, 0.74, 0.017 ],
    [ 1.23, 0.73, 0.018 ],
    [ 1.232, 0.74, 0.017 ],
    [ 1.24, 0.73, 0.018 ],
    [ 1.242, 0.74, 0.017 ],
    [ 1.25, 0.73, 0.018 ],
    [ 1.252, 0.74, 0.017 ],
    [ 1.26, 0.73, 0.018 ],
    [ 1.262, 0.74, 0.017 ],
    [ 1.27, 0.73, 0.018 ],
    [ 1.272, 0.74, 0.017 ],
    [ 1.28, 0.73, 0.018 ],
    [ 1.282, 0.74, 0.017 ],
    [ 1.29, 0.73, 0.018 ],
    [ 1.292, 0.74, 0.017 ],
    [ 1.301, 0.74, 0.017 ],
    [ 1.311, 0.74, 0.017 ],
    [ 1.321, 0.74, 0.017 ],
    [ 1.331, 0.74, 0.017 ],
    [ 1.341, 0.74, 0.017 ],
    [ 1.351, 0.74, 0.017 ],
    [ 1.361, 0.74, 0.017 ],
    [ 1.371, 0.74, 0.017 ],
    [ 1.381, 0.74, 0.017 ]
  ],
  "0.243": [
    [ 0.244, 0.69, 0.013 ],
    [ 0.249, 0.69, 0.014 ],
    [ 0.26, 0.69, 0.015 ],
    [ 0.262, 0.7, 0.014 ],
    [ 0.265, 0.69, 0.015 ],
    [ 0.272, 0.7, 0.014 ],
    [ 0.277, 0.7, 0.015 ],
    [ 0.282, 0.7, 0.015 ],
    [ 0.292, 0.71, 0.014 ],
    [ 0.294, 0.7, 0.015 ],
    [ 0.298, 0.7, 0.016 ],
    [ 0.302, 0.71, 0.014 ],
    [ 0.304, 0.7, 0.016 ],
    [ 0.306, 0.71, 0.015 ],
    [ 0.309, 0.7, 0.016 ],
    [ 0.312, 0.71, 0.015 ],
    [ 0.32, 0.7, 0.017 ],
    [ 0.322, 0.71, 0.015 ],
    [ 0.324, 0.71, 0.016 ],
    [ 0.349, 0.71, 0.017 ],
    [ 0.352, 0.71, 0.016 ],
    [ 0.355, 0.71, 0.017 ],
    [ 0.363, 0.72, 0.016 ],
    [ 0.367, 0.71, 0.017 ],
    [ 0.372, 0.72, 0.016 ],
    [ 0.379, 0.71, 0.017 ],
    [ 0.382, 0.72, 0.016 ],
    [ 0.389, 0.71, 0.018 ],
    [ 0.392, 0.72, 0.016 ],
    [ 0.394, 0.72, 0.017 ],
    [ 0.441, 0.72, 0.017 ],
    [ 0.447, 0.72, 0.018 ],
    [ 0.452, 0.72, 0.018 ],
    [ 0.472, 0.72, 0.018 ],
    [ 0.482, 0.73, 0.017 ],
    [ 0.484, 0.72, 0.018 ],
    [ 0.492, 0.73, 0.017 ],
    [ 0.495, 0.72, 0.018 ],
    [ 0.501, 0.73, 0.017 ],
    [ 0.507, 0.72, 0.018 ],
    [ 0.511, 0.73, 0.017 ],
    [ 0.518, 0.72, 0.018 ],
    [ 0.521, 0.73, 0.017 ],
    [ 0.529, 0.72, 0.019 ],
    [ 0.531, 0.73, 0.017 ],
    [ 0.536, 0.73, 0.018 ],
    [ 0.541, 0.73, 0.017 ],
    [ 0.544, 0.73, 0.018 ],
    [ 0.69, 0.73, 0.018 ],
    [ 0.699, 0.73, 0.019 ],
    [ 0.701, 0.73, 0.018 ],
    [ 0.707, 0.73, 0.019 ],
    [ 0.711, 0.73, 0.018 ],
    [ 0.714, 0.73, 0.019 ],
    [ 0.721, 0.73, 0.018 ],
    [ 0.723, 0.73, 0.019 ],
    [ 1.001, 0.73, 0.019 ],
    [ 1.022, 0.73, 0.019 ],
    [ 1.032, 0.73, 0.019 ],
    [ 1.042, 0.74, 0.018 ],
    [ 1.044, 0.73, 0.019 ],
    [ 1.052, 0.74, 0.018 ],
    [ 1.054, 0.73, 0.019 ],
    [ 1.062, 0.74, 0.018 ],
    [ 1.065, 0.73, 0.019 ],
    [ 1.072, 0.74, 0.018 ],
    [ 1.075, 0.73, 0.019 ],
    [ 1.082, 0.74, 0.018 ],
    [ 1.084, 0.73, 0.019 ],
    [ 1.092, 0.74, 0.018 ],
    [ 1.094, 0.73, 0.019 ],
    [ 1.102, 0.74, 0.018 ],
    [ 1.104, 0.73, 0.019 ],
    [ 1.112, 0.74, 0.018 ],
    [ 1.115, 0.73, 0.019 ],
    [ 1.122, 0.74, 0.018 ],
    [ 1.125, 0.73, 0.019 ],
    [ 1.132, 0.74, 0.018 ],
    [ 1.136, 0.73, 0.019 ],
    [ 1.142, 0.74, 0.018 ],
    [ 1.146, 0.73, 0.019 ],
    [ 1.152, 0.74, 0.018 ],
    [ 1.156, 0.73, 0.019 ],
    [ 1.162, 0.74, 0.018 ],
    [ 1.167, 0.73, 0.019 ],
    [ 1.172, 0.74, 0.018 ],
    [ 1.177, 0.73, 0.019 ],
    [ 1.182, 0.74, 0.018 ],
    [ 1.187, 0.73, 0.019 ],
    [ 1.192, 0.74, 0.018 ],
    [ 1.198, 0.73, 0.019 ],
    [ 1.202, 0.74, 0.018 ],
    [ 1.208, 0.73, 0.019 ],
    [ 1.212, 0.74, 0.018 ],
    [ 1.218, 0.73, 0.019 ],
    [ 1.222, 0.74, 0.018 ],
    [ 1.227, 0.73, 0.019 ],
    [ 1.232, 0.74, 0.018 ],
    [ 1.238, 0.73, 0.019 ],
    [ 1.242, 0.74, 0.018 ],
    [ 1.248, 0.73, 0.019 ],
    [ 1.252, 0.74, 0.018 ],
    [ 1.259, 0.73, 0.019 ],
    [ 1.262, 0.74, 0.018 ],
    [ 1.269, 0.74, 0.019 ],
    [ 1.272, 0.74, 0.018 ],
    [ 1.279, 0.74, 0.019 ],
    [ 1.282, 0.74, 0.018 ],
    [ 1.288, 0.74, 0.019 ],
    [ 1.292, 0.74, 0.018 ],
    [ 1.298, 0.74, 0.019 ],
    [ 1.302, 0.74, 0.018 ],
    [ 1.307, 0.74, 0.019 ],
    [ 1.312, 0.74, 0.018 ],
    [ 1.316, 0.74, 0.019 ],
    [ 1.322, 0.74, 0.018 ],
    [ 1.326, 0.74, 0.019 ],
    [ 1.332, 0.74, 0.018 ],
    [ 1.335, 0.74, 0.019 ],
    [ 1.342, 0.74, 0.018 ],
    [ 1.345, 0.74, 0.019 ],
    [ 1.352, 0.74, 0.018 ],
    [ 1.354, 0.74, 0.019 ],
    [ 1.362, 0.74, 0.018 ],
    [ 1.364, 0.74, 0.019 ],
    [ 1.372, 0.74, 0.019 ],
    [ 1.382, 0.74, 0.019 ]
  ],
  "0.257": [
    [ 0.258, 0.69, 0.014 ],
    [ 0.265, 0.69, 0.015 ],
    [ 0.278, 0.69, 0.016 ],
    [ 0.282, 0.7, 0.015 ],
    [ 0.288, 0.69, 0.016 ],
    [ 0.291, 0.7, 0.015 ],
    [ 0.298, 0.7, 0.016 ],
    [ 0.312, 0.71, 0.015 ],
    [ 0.316, 0.7, 0.017 ],
    [ 0.322, 0.71, 0.015 ],
    [ 0.324, 0.7, 0.017 ],
    [ 0.329, 0.71, 0.016 ],
    [ 0.341, 0.71, 0.016 ],
    [ 0.345, 0.71, 0.017 ],
    [ 0.37, 0.71, 0.018 ],
    [ 0.372, 0.72, 0.016 ],
    [ 0.374, 0.71, 0.018 ],
    [ 0.382, 0.71, 0.018 ],
    [ 0.392, 0.72, 0.017 ],
    [ 0.401, 0.72, 0.017 ],
    [ 0.411, 0.72, 0.017 ],
    [ 0.417, 0.72, 0.018 ],
    [ 0.422, 0.72, 0.018 ],
    [ 0.469, 0.72, 0.019 ],
    [ 0.472, 0.72, 0.018 ],
    [ 0.475, 0.72, 0.019 ],
    [ 0.501, 0.73, 0.018 ],
    [ 0.504, 0.72, 0.019 ],
    [ 0.511, 0.73, 0.018 ],
    [ 0.516, 0.72, 0.019 ],
    [ 0.521, 0.73, 0.018 ],
    [ 0.527, 0.72, 0.019 ],
    [ 0.531, 0.73, 0.018 ],
    [ 0.539, 0.72, 0.019 ],
    [ 0.541, 0.73, 0.018 ],
    [ 0.55, 0.73, 0.018 ],
    [ 0.558, 0.73, 0.019 ],
    [ 0.56, 0.73, 0.018 ],
    [ 0.567, 0.73, 0.019 ],
    [ 0.571, 0.73, 0.018 ],
    [ 0.574, 0.73, 0.019 ],
    [ 0.72, 0.73, 0.019 ],
    [ 0.727, 0.73, 0.02 ],
    [ 0.731, 0.73, 0.019 ],
    [ 0.735, 0.73, 0.02 ],
    [ 0.741, 0.73, 0.019 ],
    [ 0.744, 0.73, 0.02 ],
    [ 0.751, 0.73, 0.019 ],
    [ 0.753, 0.73, 0.02 ],
    [ 1.042, 0.73, 0.02 ],
    [ 1.052, 0.73, 0.02 ],
    [ 1.062, 0.74, 0.019 ],
    [ 1.064, 0.73, 0.02 ],
    [ 1.072, 0.74, 0.019 ],
    [ 1.074, 0.73, 0.02 ],
    [ 1.082, 0.74, 0.019 ],
    [ 1.084, 0.73, 0.02 ],
    [ 1.092, 0.74, 0.019 ],
    [ 1.094, 0.73, 0.02 ],
    [ 1.102, 0.74, 0.019 ],
    [ 1.104, 0.73, 0.02 ],
    [ 1.112, 0.74, 0.019 ],
    [ 1.114, 0.73, 0.02 ],
    [ 1.122, 0.74, 0.019 ],
    [ 1.125, 0.73, 0.02 ],
    [ 1.132, 0.74, 0.019 ],
    [ 1.135, 0.73, 0.02 ],
    [ 1.142, 0.74, 0.019 ],
    [ 1.146, 0.73, 0.02 ],
    [ 1.152, 0.74, 0.019 ],
    [ 1.156, 0.73, 0.02 ],
    [ 1.162, 0.74, 0.019 ],
    [ 1.167, 0.73, 0.02 ],
    [ 1.172, 0.74, 0.019 ],
    [ 1.177, 0.73, 0.02 ],
    [ 1.182, 0.74, 0.019 ],
    [ 1.188, 0.73, 0.02 ],
    [ 1.192, 0.74, 0.019 ],
    [ 1.198, 0.73, 0.02 ],
    [ 1.201, 0.74, 0.019 ],
    [ 1.209, 0.73, 0.021 ],
    [ 1.212, 0.74, 0.019 ],
    [ 1.219, 0.73, 0.021 ],
    [ 1.222, 0.74, 0.019 ],
    [ 1.229, 0.73, 0.021 ],
    [ 1.232, 0.74, 0.019 ],
    [ 1.238, 0.73, 0.021 ],
    [ 1.242, 0.74, 0.019 ],
    [ 1.248, 0.73, 0.021 ],
    [ 1.252, 0.74, 0.019 ],
    [ 1.258, 0.73, 0.021 ],
    [ 1.262, 0.74, 0.019 ],
    [ 1.268, 0.73, 0.021 ],
    [ 1.272, 0.74, 0.019 ],
    [ 1.277, 0.73, 0.021 ],
    [ 1.282, 0.74, 0.019 ],
    [ 1.287, 0.73, 0.021 ],
    [ 1.292, 0.74, 0.019 ],
    [ 1.297, 0.73, 0.021 ],
    [ 1.302, 0.74, 0.019 ],
    [ 1.306, 0.74, 0.02 ],
    [ 1.308, 0.73, 0.021 ],
    [ 1.312, 0.74, 0.019 ],
    [ 1.314, 0.74, 0.02 ],
    [ 1.318, 0.73, 0.021 ],
    [ 1.322, 0.74, 0.019 ],
    [ 1.324, 0.74, 0.02 ],
    [ 1.328, 0.73, 0.021 ],
    [ 1.332, 0.74, 0.019 ],
    [ 1.334, 0.74, 0.02 ],
    [ 1.338, 0.73, 0.021 ],
    [ 1.342, 0.74, 0.02 ],
    [ 1.349, 0.73, 0.021 ],
    [ 1.352, 0.74, 0.02 ],
    [ 1.359, 0.73, 0.021 ],
    [ 1.362, 0.74, 0.02 ],
    [ 1.369, 0.73, 0.021 ],
    [ 1.372, 0.74, 0.02 ],
    [ 1.379, 0.73, 0.021 ],
    [ 1.382, 0.74, 0.02 ],
    [ 1.389, 0.73, 0.021 ],
    [ 1.392, 0.74, 0.02 ],
    [ 1.399, 0.73, 0.021 ],
    [ 1.402, 0.74, 0.02 ],
    [ 1.409, 0.73, 0.021 ],
    [ 1.412, 0.74, 0.02 ],
    [ 1.419, 0.73, 0.021 ],
    [ 1.422, 0.74, 0.02 ],
    [ 1.43, 0.73, 0.021 ],
    [ 1.432, 0.74, 0.02 ],
    [ 1.441, 0.74, 0.02 ],
    [ 1.451, 0.74, 0.02 ],
    [ 1.461, 0.74, 0.02 ],
    [ 1.471, 0.74, 0.02 ],
    [ 1.481, 0.74, 0.02 ],
    [ 1.491, 0.74, 0.02 ],
    [ 1.501, 0.74, 0.02 ],
    [ 1.511, 0.74, 0.02 ],
    [ 1.521, 0.74, 0.02 ]
  ],
  "0.264": [
    [ 0.265, 0.69, 0.014 ],
    [ 0.267, 0.69, 0.015 ],
    [ 0.279, 0.69, 0.016 ],
    [ 0.282, 0.69, 0.015 ],
    [ 0.284, 0.69, 0.016 ],
    [ 0.292, 0.7, 0.015 ],
    [ 0.297, 0.7, 0.016 ],
    [ 0.3, 0.69, 0.017 ],
    [ 0.302, 0.7, 0.015 ],
    [ 0.304, 0.7, 0.016 ],
    [ 0.318, 0.7, 0.017 ],
    [ 0.332, 0.71, 0.016 ],
    [ 0.339, 0.7, 0.018 ],
    [ 0.342, 0.71, 0.017 ],
    [ 0.35, 0.71, 0.017 ],
    [ 0.367, 0.71, 0.018 ],
    [ 0.392, 0.72, 0.017 ],
    [ 0.398, 0.71, 0.019 ],
    [ 0.402, 0.72, 0.017 ],
    [ 0.407, 0.71, 0.019 ],
    [ 0.412, 0.72, 0.018 ],
    [ 0.419, 0.71, 0.019 ],
    [ 0.422, 0.72, 0.018 ],
    [ 0.45, 0.72, 0.019 ],
    [ 0.452, 0.72, 0.018 ],
    [ 0.456, 0.72, 0.019 ],
    [ 0.501, 0.72, 0.019 ],
    [ 0.511, 0.73, 0.018 ],
    [ 0.514, 0.72, 0.019 ],
    [ 0.518, 0.72, 0.02 ],
    [ 0.521, 0.73, 0.018 ],
    [ 0.525, 0.72, 0.02 ],
    [ 0.531, 0.73, 0.018 ],
    [ 0.534, 0.72, 0.02 ],
    [ 0.541, 0.73, 0.019 ],
    [ 0.545, 0.72, 0.02 ],
    [ 0.551, 0.73, 0.019 ],
    [ 0.557, 0.72, 0.02 ],
    [ 0.561, 0.73, 0.019 ],
    [ 0.569, 0.72, 0.02 ],
    [ 0.571, 0.73, 0.019 ],
    [ 0.63, 0.73, 0.019 ],
    [ 0.639, 0.73, 0.02 ],
    [ 0.641, 0.73, 0.019 ],
    [ 0.648, 0.73, 0.02 ],
    [ 0.651, 0.73, 0.019 ],
    [ 0.654, 0.73, 0.02 ],
    [ 0.88, 0.73, 0.02 ],
    [ 0.888, 0.73, 0.021 ],
    [ 0.891, 0.73, 0.02 ],
    [ 0.897, 0.73, 0.021 ],
    [ 0.901, 0.73, 0.02 ],
    [ 0.906, 0.73, 0.021 ],
    [ 0.911, 0.73, 0.02 ],
    [ 0.914, 0.73, 0.021 ],
    [ 0.921, 0.73, 0.02 ],
    [ 0.923, 0.73, 0.021 ],
    [ 1.132, 0.73, 0.021 ],
    [ 1.142, 0.73, 0.021 ],
    [ 1.152, 0.74, 0.02 ],
    [ 1.154, 0.73, 0.021 ],
    [ 1.162, 0.74, 0.02 ],
    [ 1.164, 0.73, 0.021 ],
    [ 1.172, 0.74, 0.02 ],
    [ 1.174, 0.73, 0.021 ],
    [ 1.182, 0.73, 0.021 ],
    [ 1.192, 0.74, 0.02 ],
    [ 1.194, 0.73, 0.021 ],
    [ 1.202, 0.74, 0.02 ],
    [ 1.204, 0.73, 0.021 ],
    [ 1.212, 0.74, 0.02 ],
    [ 1.215, 0.73, 0.021 ],
    [ 1.222, 0.74, 0.02 ],
    [ 1.225, 0.73, 0.021 ],
    [ 1.232, 0.74, 0.02 ],
    [ 1.235, 0.73, 0.021 ],
    [ 1.242, 0.74, 0.02 ],
    [ 1.246, 0.73, 0.021 ],
    [ 1.252, 0.74, 0.02 ],
    [ 1.256, 0.73, 0.021 ],
    [ 1.262, 0.74, 0.02 ],
    [ 1.267, 0.73, 0.021 ],
    [ 1.272, 0.74, 0.02 ],
    [ 1.277, 0.73, 0.021 ],
    [ 1.282, 0.74, 0.02 ],
    [ 1.287, 0.73, 0.021 ],
    [ 1.292, 0.74, 0.02 ],
    [ 1.297, 0.73, 0.021 ],
    [ 1.302, 0.74, 0.02 ],
    [ 1.307, 0.73, 0.021 ],
    [ 1.312, 0.74, 0.02 ],
    [ 1.317, 0.73, 0.021 ],
    [ 1.322, 0.74, 0.02 ],
    [ 1.328, 0.73, 0.021 ],
    [ 1.332, 0.74, 0.02 ],
    [ 1.338, 0.73, 0.021 ],
    [ 1.342, 0.74, 0.02 ],
    [ 1.348, 0.73, 0.021 ],
    [ 1.352, 0.74, 0.02 ],
    [ 1.359, 0.73, 0.021 ],
    [ 1.362, 0.74, 0.02 ],
    [ 1.369, 0.73, 0.021 ],
    [ 1.372, 0.74, 0.02 ],
    [ 1.379, 0.73, 0.021 ],
    [ 1.382, 0.74, 0.02 ],
    [ 1.39, 0.73, 0.021 ],
    [ 1.392, 0.74, 0.02 ],
    [ 1.4, 0.73, 0.021 ],
    [ 1.402, 0.74, 0.02 ],
    [ 1.41, 0.73, 0.021 ],
    [ 1.412, 0.74, 0.02 ],
    [ 1.421, 0.74, 0.02 ],
    [ 1.431, 0.74, 0.02 ],
    [ 1.441, 0.74, 0.02 ],
    [ 1.451, 0.74, 0.02 ]
  ],
  "0.277": [
    [ 0.278, 0.69, 0.015 ],
    [ 0.285, 0.69, 0.016 ],
    [ 0.292, 0.69, 0.016 ],
    [ 0.298, 0.69, 0.017 ],
    [ 0.302, 0.7, 0.015 ],
    [ 0.304, 0.7, 0.016 ],
    [ 0.308, 0.69, 0.017 ],
    [ 0.311, 0.7, 0.016 ],
    [ 0.317, 0.7, 0.017 ],
    [ 0.336, 0.7, 0.018 ],
    [ 0.342, 0.71, 0.017 ],
    [ 0.344, 0.7, 0.018 ],
    [ 0.352, 0.71, 0.017 ],
    [ 0.359, 0.7, 0.019 ],
    [ 0.362, 0.71, 0.017 ],
    [ 0.365, 0.71, 0.018 ],
    [ 0.389, 0.71, 0.019 ],
    [ 0.392, 0.71, 0.019 ],
    [ 0.412, 0.71, 0.019 ],
    [ 0.419, 0.71, 0.019 ],
    [ 0.421, 0.72, 0.018 ],
    [ 0.428, 0.71, 0.02 ],
    [ 0.432, 0.72, 0.018 ],
    [ 0.435, 0.72, 0.019 ],
    [ 0.44, 0.71, 0.02 ],
    [ 0.442, 0.72, 0.019 ],
    [ 0.479, 0.72, 0.02 ],
    [ 0.482, 0.72, 0.019 ],
    [ 0.485, 0.72, 0.02 ],
    [ 0.531, 0.73, 0.019 ],
    [ 0.534, 0.72, 0.02 ],
    [ 0.541, 0.73, 0.019 ],
    [ 0.546, 0.72, 0.02 ],
    [ 0.548, 0.72, 0.021 ],
    [ 0.551, 0.73, 0.019 ],
    [ 0.556, 0.72, 0.021 ],
    [ 0.561, 0.73, 0.019 ],
    [ 0.565, 0.72, 0.021 ],
    [ 0.571, 0.73, 0.019 ],
    [ 0.573, 0.73, 0.02 ],
    [ 0.577, 0.72, 0.021 ],
    [ 0.581, 0.73, 0.02 ],
    [ 0.588, 0.72, 0.021 ],
    [ 0.591, 0.73, 0.02 ],
    [ 0.6, 0.73, 0.02 ],
    [ 0.678, 0.73, 0.021 ],
    [ 0.681, 0.73, 0.02 ],
    [ 0.685, 0.73, 0.021 ],
    [ 0.691, 0.73, 0.02 ],
    [ 0.694, 0.73, 0.021 ],
    [ 0.701, 0.73, 0.021 ],
    [ 0.92, 0.73, 0.021 ],
    [ 0.928, 0.73, 0.022 ],
    [ 0.931, 0.73, 0.021 ],
    [ 0.937, 0.73, 0.022 ],
    [ 0.941, 0.73, 0.021 ],
    [ 0.945, 0.73, 0.022 ],
    [ 0.951, 0.73, 0.021 ],
    [ 0.954, 0.73, 0.022 ],
    [ 0.961, 0.73, 0.022 ],
    [ 1.212, 0.73, 0.022 ],
    [ 1.222, 0.73, 0.022 ],
    [ 1.232, 0.74, 0.021 ],
    [ 1.234, 0.73, 0.022 ],
    [ 1.242, 0.74, 0.021 ],
    [ 1.244, 0.73, 0.022 ],
    [ 1.252, 0.74, 0.021 ],
    [ 1.255, 0.73, 0.022 ],
    [ 1.262, 0.74, 0.021 ],
    [ 1.264, 0.73, 0.022 ],
    [ 1.272, 0.74, 0.021 ],
    [ 1.274, 0.73, 0.022 ],
    [ 1.282, 0.74, 0.021 ],
    [ 1.284, 0.73, 0.022 ],
    [ 1.292, 0.74, 0.021 ],
    [ 1.295, 0.73, 0.022 ],
    [ 1.302, 0.74, 0.021 ],
    [ 1.305, 0.73, 0.022 ],
    [ 1.312, 0.74, 0.021 ],
    [ 1.316, 0.73, 0.022 ],
    [ 1.322, 0.74, 0.021 ],
    [ 1.326, 0.73, 0.022 ],
    [ 1.332, 0.74, 0.021 ],
    [ 1.337, 0.73, 0.022 ],
    [ 1.342, 0.74, 0.021 ],
    [ 1.347, 0.73, 0.022 ],
    [ 1.352, 0.74, 0.021 ],
    [ 1.358, 0.73, 0.022 ],
    [ 1.362, 0.74, 0.021 ],
    [ 1.368, 0.73, 0.022 ],
    [ 1.372, 0.74, 0.021 ],
    [ 1.379, 0.73, 0.022 ],
    [ 1.382, 0.74, 0.021 ],
    [ 1.389, 0.73, 0.022 ],
    [ 1.392, 0.74, 0.021 ],
    [ 1.399, 0.73, 0.022 ],
    [ 1.402, 0.74, 0.021 ],
    [ 1.409, 0.73, 0.022 ],
    [ 1.412, 0.74, 0.021 ],
    [ 1.419, 0.73, 0.022 ],
    [ 1.422, 0.74, 0.021 ],
    [ 1.43, 0.73, 0.022 ],
    [ 1.432, 0.74, 0.021 ],
    [ 1.44, 0.73, 0.022 ],
    [ 1.442, 0.74, 0.021 ],
    [ 1.451, 0.74, 0.021 ],
    [ 1.461, 0.74, 0.021 ],
    [ 1.471, 0.74, 0.021 ]
  ],
  "0.284": [
    [ 0.285, 0.69, 0.016 ],
    [ 0.298, 0.69, 0.017 ],
    [ 0.302, 0.69, 0.016 ],
    [ 0.304, 0.69, 0.017 ],
    [ 0.311, 0.7, 0.016 ],
    [ 0.317, 0.7, 0.017 ],
    [ 0.321, 0.7, 0.017 ],
    [ 0.335, 0.7, 0.018 ],
    [ 0.352, 0.7, 0.018 ],
    [ 0.355, 0.71, 0.017 ],
    [ 0.357, 0.7, 0.019 ],
    [ 0.362, 0.71, 0.017 ],
    [ 0.364, 0.71, 0.018 ],
    [ 0.369, 0.7, 0.019 ],
    [ 0.372, 0.71, 0.018 ],
    [ 0.388, 0.71, 0.019 ],
    [ 0.417, 0.71, 0.02 ],
    [ 0.422, 0.72, 0.018 ],
    [ 0.424, 0.71, 0.02 ],
    [ 0.432, 0.72, 0.019 ],
    [ 0.437, 0.71, 0.02 ],
    [ 0.442, 0.72, 0.019 ],
    [ 0.46, 0.71, 0.021 ],
    [ 0.462, 0.72, 0.019 ],
    [ 0.467, 0.72, 0.02 ],
    [ 0.472, 0.72, 0.02 ],
    [ 0.52, 0.72, 0.021 ],
    [ 0.522, 0.72, 0.02 ],
    [ 0.526, 0.72, 0.021 ],
    [ 0.532, 0.72, 0.021 ],
    [ 0.562, 0.72, 0.021 ],
    [ 0.572, 0.73, 0.02 ],
    [ 0.575, 0.72, 0.021 ],
    [ 0.582, 0.73, 0.02 ],
    [ 0.587, 0.72, 0.021 ],
    [ 0.592, 0.73, 0.02 ],
    [ 0.601, 0.73, 0.02 ],
    [ 0.61, 0.72, 0.022 ],
    [ 0.612, 0.73, 0.02 ],
    [ 0.619, 0.72, 0.022 ],
    [ 0.622, 0.73, 0.02 ],
    [ 0.627, 0.73, 0.021 ],
    [ 0.629, 0.72, 0.022 ],
    [ 0.632, 0.73, 0.02 ],
    [ 0.635, 0.73, 0.021 ],
    [ 0.641, 0.73, 0.02 ],
    [ 0.643, 0.73, 0.021 ],
    [ 0.771, 0.73, 0.021 ],
    [ 0.78, 0.73, 0.022 ],
    [ 0.782, 0.73, 0.021 ],
    [ 0.789, 0.73, 0.022 ],
    [ 0.792, 0.73, 0.021 ],
    [ 0.797, 0.73, 0.022 ],
    [ 0.802, 0.73, 0.022 ],
    [ 1.122, 0.73, 0.022 ],
    [ 1.132, 0.74, 0.021 ],
    [ 1.134, 0.73, 0.022 ],
    [ 1.142, 0.74, 0.021 ],
    [ 1.144, 0.73, 0.022 ],
    [ 1.152, 0.74, 0.021 ],
    [ 1.155, 0.73, 0.022 ],
    [ 1.162, 0.74, 0.021 ],
    [ 1.166, 0.73, 0.022 ],
    [ 1.172, 0.74, 0.021 ],
    [ 1.176, 0.73, 0.022 ],
    [ 1.182, 0.74, 0.021 ],
    [ 1.187, 0.73, 0.022 ],
    [ 1.191, 0.74, 0.021 ],
    [ 1.198, 0.73, 0.023 ],
    [ 1.202, 0.74, 0.021 ],
    [ 1.208, 0.73, 0.023 ],
    [ 1.212, 0.74, 0.021 ],
    [ 1.218, 0.73, 0.023 ],
    [ 1.222, 0.74, 0.021 ],
    [ 1.227, 0.73, 0.023 ],
    [ 1.232, 0.74, 0.021 ],
    [ 1.237, 0.73, 0.023 ],
    [ 1.242, 0.74, 0.021 ],
    [ 1.247, 0.73, 0.023 ],
    [ 1.252, 0.74, 0.021 ],
    [ 1.257, 0.73, 0.023 ],
    [ 1.262, 0.74, 0.021 ],
    [ 1.266, 0.73, 0.023 ],
    [ 1.272, 0.74, 0.021 ],
    [ 1.276, 0.73, 0.023 ],
    [ 1.282, 0.74, 0.021 ],
    [ 1.286, 0.73, 0.023 ],
    [ 1.292, 0.74, 0.021 ],
    [ 1.296, 0.73, 0.023 ],
    [ 1.302, 0.74, 0.021 ],
    [ 1.306, 0.73, 0.023 ],
    [ 1.312, 0.74, 0.021 ],
    [ 1.315, 0.73, 0.023 ],
    [ 1.322, 0.74, 0.021 ],
    [ 1.325, 0.73, 0.023 ],
    [ 1.332, 0.74, 0.021 ],
    [ 1.335, 0.73, 0.023 ],
    [ 1.342, 0.74, 0.021 ],
    [ 1.344, 0.73, 0.023 ],
    [ 1.352, 0.74, 0.021 ],
    [ 1.354, 0.73, 0.023 ],
    [ 1.362, 0.74, 0.021 ],
    [ 1.364, 0.73, 0.023 ],
    [ 1.372, 0.74, 0.021 ],
    [ 1.374, 0.73, 0.023 ],
    [ 1.382, 0.74, 0.021 ],
    [ 1.384, 0.73, 0.023 ],
    [ 1.392, 0.73, 0.023 ],
    [ 1.402, 0.73, 0.023 ],
    [ 1.412, 0.73, 0.023 ],
    [ 1.422, 0.73, 0.023 ],
    [ 1.432, 0.74, 0.022 ],
    [ 1.434, 0.73, 0.023 ],
    [ 1.442, 0.74, 0.022 ],
    [ 1.444, 0.73, 0.023 ],
    [ 1.452, 0.74, 0.022 ],
    [ 1.454, 0.73, 0.023 ],
    [ 1.462, 0.74, 0.022 ],
    [ 1.464, 0.73, 0.023 ],
    [ 1.472, 0.74, 0.022 ],
    [ 1.475, 0.73, 0.023 ],
    [ 1.482, 0.74, 0.022 ],
    [ 1.485, 0.73, 0.023 ],
    [ 1.492, 0.74, 0.022 ],
    [ 1.495, 0.73, 0.023 ],
    [ 1.502, 0.74, 0.022 ],
    [ 1.506, 0.73, 0.023 ],
    [ 1.512, 0.74, 0.022 ],
    [ 1.516, 0.73, 0.023 ],
    [ 1.522, 0.74, 0.022 ],
    [ 1.526, 0.73, 0.023 ],
    [ 1.532, 0.74, 0.022 ],
    [ 1.536, 0.73, 0.023 ],
    [ 1.542, 0.74, 0.022 ],
    [ 1.547, 0.73, 0.023 ],
    [ 1.552, 0.74, 0.022 ],
    [ 1.557, 0.73, 0.023 ],
    [ 1.562, 0.74, 0.022 ],
    [ 1.567, 0.73, 0.023 ],
    [ 1.572, 0.74, 0.022 ],
    [ 1.577, 0.73, 0.023 ],
    [ 1.582, 0.74, 0.022 ],
    [ 1.587, 0.73, 0.023 ],
    [ 1.592, 0.74, 0.022 ],
    [ 1.598, 0.73, 0.023 ],
    [ 1.602, 0.74, 0.022 ],
    [ 1.608, 0.73, 0.023 ],
    [ 1.612, 0.74, 0.022 ],
    [ 1.618, 0.73, 0.023 ],
    [ 1.622, 0.74, 0.022 ],
    [ 1.629, 0.73, 0.023 ],
    [ 1.632, 0.74, 0.022 ],
    [ 1.639, 0.73, 0.023 ],
    [ 1.642, 0.74, 0.022 ],
    [ 1.65, 0.73, 0.023 ],
    [ 1.652, 0.74, 0.022 ],
    [ 1.659, 0.73, 0.023 ],
    [ 1.662, 0.74, 0.022 ],
    [ 1.67, 0.73, 0.023 ],
    [ 1.672, 0.74, 0.022 ],
    [ 1.68, 0.73, 0.023 ],
    [ 1.682, 0.74, 0.022 ],
    [ 1.69, 0.73, 0.023 ],
    [ 1.692, 0.74, 0.022 ],
    [ 1.7, 0.73, 0.023 ],
    [ 1.702, 0.74, 0.022 ],
    [ 1.71, 0.73, 0.023 ],
    [ 1.712, 0.74, 0.022 ],
    [ 1.721, 0.74, 0.022 ],
    [ 1.731, 0.74, 0.022 ]
  ],
  "0.308": [
    [ 0.309, 0.69, 0.017 ],
    [ 0.318, 0.69, 0.018 ],
    [ 0.322, 0.69, 0.018 ],
    [ 0.331, 0.7, 0.017 ],
    [ 0.336, 0.69, 0.019 ],
    [ 0.342, 0.7, 0.018 ],
    [ 0.351, 0.7, 0.018 ],
    [ 0.355, 0.7, 0.019 ],
    [ 0.37, 0.7, 0.02 ],
    [ 0.372, 0.7, 0.019 ],
    [ 0.375, 0.7, 0.02 ],
    [ 0.382, 0.7, 0.02 ],
    [ 0.385, 0.7, 0.02 ],
    [ 0.392, 0.71, 0.019 ],
    [ 0.399, 0.7, 0.021 ],
    [ 0.402, 0.71, 0.019 ],
    [ 0.406, 0.71, 0.02 ],
    [ 0.429, 0.71, 0.021 ],
    [ 0.432, 0.71, 0.02 ],
    [ 0.434, 0.71, 0.021 ],
    [ 0.453, 0.71, 0.021 ],
    [ 0.461, 0.72, 0.02 ],
    [ 0.467, 0.71, 0.022 ],
    [ 0.472, 0.72, 0.02 ],
    [ 0.476, 0.71, 0.022 ],
    [ 0.482, 0.72, 0.02 ],
    [ 0.484, 0.72, 0.021 ],
    [ 0.52, 0.72, 0.022 ],
    [ 0.522, 0.72, 0.021 ],
    [ 0.526, 0.72, 0.022 ],
    [ 0.532, 0.72, 0.022 ],
    [ 0.587, 0.72, 0.023 ],
    [ 0.592, 0.72, 0.022 ],
    [ 0.594, 0.72, 0.023 ],
    [ 0.602, 0.72, 0.023 ],
    [ 0.632, 0.73, 0.022 ],
    [ 0.635, 0.72, 0.023 ],
    [ 0.642, 0.73, 0.022 ],
    [ 0.651, 0.73, 0.022 ],
    [ 0.701, 0.73, 0.022 ],
    [ 0.709, 0.73, 0.023 ],
    [ 0.712, 0.73, 0.022 ],
    [ 0.716, 0.73, 0.023 ],
    [ 0.722, 0.73, 0.022 ],
    [ 0.725, 0.73, 0.023 ],
    [ 0.891, 0.73, 0.023 ],
    [ 0.898, 0.73, 0.024 ],
    [ 0.902, 0.73, 0.023 ],
    [ 0.906, 0.73, 0.024 ],
    [ 0.912, 0.73, 0.023 ],
    [ 0.915, 0.73, 0.024 ],
    [ 0.922, 0.73, 0.023 ],
    [ 0.924, 0.73, 0.024 ],
    [ 1.262, 0.73, 0.024 ],
    [ 1.272, 0.74, 0.023 ],
    [ 1.274, 0.73, 0.024 ],
    [ 1.282, 0.74, 0.023 ],
    [ 1.285, 0.73, 0.024 ],
    [ 1.292, 0.74, 0.023 ],
    [ 1.296, 0.73, 0.024 ],
    [ 1.302, 0.74, 0.023 ],
    [ 1.306, 0.73, 0.024 ],
    [ 1.312, 0.74, 0.023 ],
    [ 1.317, 0.73, 0.024 ],
    [ 1.322, 0.74, 0.023 ],
    [ 1.328, 0.73, 0.024 ],
    [ 1.332, 0.74, 0.023 ],
    [ 1.339, 0.73, 0.024 ],
    [ 1.342, 0.74, 0.023 ],
    [ 1.35, 0.73, 0.024 ],
    [ 1.352, 0.74, 0.023 ],
    [ 1.36, 0.73, 0.025 ],
    [ 1.362, 0.74, 0.023 ],
    [ 1.371, 0.74, 0.023 ],
    [ 1.381, 0.74, 0.023 ],
    [ 1.391, 0.74, 0.023 ],
    [ 1.4, 0.73, 0.025 ],
    [ 1.402, 0.74, 0.023 ],
    [ 1.41, 0.73, 0.025 ],
    [ 1.412, 0.74, 0.023 ],
    [ 1.42, 0.73, 0.025 ],
    [ 1.422, 0.74, 0.023 ],
    [ 1.43, 0.73, 0.025 ],
    [ 1.432, 0.74, 0.023 ],
    [ 1.439, 0.73, 0.025 ],
    [ 1.442, 0.74, 0.023 ],
    [ 1.45, 0.73, 0.025 ],
    [ 1.452, 0.74, 0.023 ],
    [ 1.459, 0.73, 0.025 ],
    [ 1.462, 0.74, 0.023 ],
    [ 1.469, 0.73, 0.025 ],
    [ 1.472, 0.74, 0.023 ],
    [ 1.479, 0.73, 0.025 ],
    [ 1.482, 0.74, 0.023 ],
    [ 1.489, 0.73, 0.025 ],
    [ 1.492, 0.74, 0.023 ],
    [ 1.498, 0.73, 0.025 ],
    [ 1.502, 0.74, 0.023 ],
    [ 1.508, 0.73, 0.025 ],
    [ 1.512, 0.74, 0.023 ],
    [ 1.518, 0.73, 0.025 ],
    [ 1.522, 0.74, 0.023 ],
    [ 1.528, 0.73, 0.025 ],
    [ 1.532, 0.74, 0.023 ],
    [ 1.538, 0.73, 0.025 ],
    [ 1.542, 0.74, 0.023 ],
    [ 1.548, 0.73, 0.025 ],
    [ 1.552, 0.74, 0.023 ],
    [ 1.558, 0.73, 0.025 ],
    [ 1.562, 0.74, 0.023 ],
    [ 1.568, 0.73, 0.025 ],
    [ 1.572, 0.74, 0.023 ],
    [ 1.577, 0.73, 0.025 ],
    [ 1.582, 0.74, 0.023 ],
    [ 1.587, 0.73, 0.025 ],
    [ 1.592, 0.74, 0.023 ],
    [ 1.597, 0.73, 0.025 ],
    [ 1.602, 0.74, 0.023 ],
    [ 1.607, 0.73, 0.025 ],
    [ 1.612, 0.74, 0.023 ],
    [ 1.617, 0.73, 0.025 ],
    [ 1.622, 0.74, 0.023 ],
    [ 1.627, 0.73, 0.025 ],
    [ 1.632, 0.74, 0.023 ],
    [ 1.637, 0.73, 0.025 ],
    [ 1.642, 0.74, 0.023 ],
    [ 1.647, 0.73, 0.025 ],
    [ 1.652, 0.74, 0.023 ],
    [ 1.656, 0.73, 0.025 ],
    [ 1.662, 0.74, 0.023 ],
    [ 1.666, 0.73, 0.025 ],
    [ 1.672, 0.74, 0.023 ],
    [ 1.676, 0.73, 0.025 ],
    [ 1.682, 0.74, 0.023 ],
    [ 1.686, 0.73, 0.025 ],
    [ 1.692, 0.74, 0.023 ],
    [ 1.696, 0.73, 0.025 ],
    [ 1.702, 0.74, 0.023 ],
    [ 1.706, 0.73, 0.025 ],
    [ 1.712, 0.74, 0.023 ],
    [ 1.716, 0.73, 0.025 ],
    [ 1.722, 0.74, 0.023 ],
    [ 1.726, 0.73, 0.025 ],
    [ 1.732, 0.74, 0.023 ],
    [ 1.736, 0.73, 0.025 ],
    [ 1.742, 0.74, 0.023 ],
    [ 1.745, 0.73, 0.025 ],
    [ 1.752, 0.74, 0.023 ],
    [ 1.755, 0.73, 0.025 ],
    [ 1.762, 0.74, 0.023 ],
    [ 1.765, 0.73, 0.025 ],
    [ 1.772, 0.74, 0.023 ],
    [ 1.775, 0.73, 0.025 ],
    [ 1.782, 0.74, 0.023 ],
    [ 1.785, 0.73, 0.025 ],
    [ 1.792, 0.74, 0.023 ],
    [ 1.795, 0.73, 0.025 ],
    [ 1.802, 0.74, 0.023 ],
    [ 1.804, 0.73, 0.025 ],
    [ 1.812, 0.74, 0.023 ],
    [ 1.814, 0.74, 0.024 ],
    [ 1.816, 0.73, 0.025 ],
    [ 1.822, 0.74, 0.024 ],
    [ 1.826, 0.73, 0.025 ],
    [ 1.832, 0.74, 0.024 ],
    [ 1.837, 0.73, 0.025 ],
    [ 1.842, 0.74, 0.024 ],
    [ 1.847, 0.73, 0.025 ],
    [ 1.852, 0.74, 0.024 ],
    [ 1.857, 0.73, 0.025 ],
    [ 1.862, 0.74, 0.024 ],
    [ 1.867, 0.73, 0.025 ],
    [ 1.872, 0.74, 0.024 ],
    [ 1.877, 0.73, 0.025 ],
    [ 1.882, 0.74, 0.024 ],
    [ 1.887, 0.73, 0.025 ],
    [ 1.892, 0.74, 0.024 ],
    [ 1.898, 0.73, 0.025 ],
    [ 1.902, 0.74, 0.024 ],
    [ 1.908, 0.73, 0.025 ],
    [ 1.912, 0.74, 0.024 ],
    [ 1.918, 0.73, 0.025 ],
    [ 1.922, 0.74, 0.024 ],
    [ 1.929, 0.73, 0.025 ],
    [ 1.932, 0.74, 0.024 ],
    [ 1.939, 0.73, 0.025 ],
    [ 1.942, 0.74, 0.024 ],
    [ 1.95, 0.73, 0.025 ],
    [ 1.952, 0.74, 0.024 ],
    [ 1.96, 0.73, 0.025 ],
    [ 1.962, 0.74, 0.024 ],
    [ 1.97, 0.73, 0.025 ],
    [ 1.972, 0.74, 0.024 ],
    [ 1.98, 0.73, 0.025 ],
    [ 1.982, 0.74, 0.024 ],
    [ 1.99, 0.73, 0.025 ],
    [ 1.992, 0.74, 0.024 ],
    [ 2, 0.73, 0.025 ],
    [ 2.002, 0.74, 0.024 ],
    [ 2.011, 0.74, 0.024 ],
    [ 2.021, 0.74, 0.024 ]
  ],
  "0.338": [
    [ 0.339, 0.68, 0.02 ],
    [ 0.342, 0.69, 0.018 ],
    [ 0.345, 0.69, 0.019 ],
    [ 0.351, 0.69, 0.019 ],
    [ 0.354, 0.69, 0.02 ],
    [ 0.367, 0.69, 0.021 ],
    [ 0.372, 0.7, 0.019 ],
    [ 0.376, 0.69, 0.021 ],
    [ 0.378, 0.7, 0.02 ],
    [ 0.389, 0.7, 0.021 ],
    [ 0.392, 0.7, 0.02 ],
    [ 0.395, 0.7, 0.021 ],
    [ 0.41, 0.7, 0.022 ],
    [ 0.412, 0.71, 0.02 ],
    [ 0.415, 0.7, 0.022 ],
    [ 0.422, 0.71, 0.02 ],
    [ 0.425, 0.7, 0.022 ],
    [ 0.428, 0.71, 0.021 ],
    [ 0.431, 0.71, 0.021 ],
    [ 0.44, 0.7, 0.023 ],
    [ 0.442, 0.71, 0.021 ],
    [ 0.446, 0.71, 0.022 ],
    [ 0.47, 0.71, 0.023 ],
    [ 0.472, 0.71, 0.022 ],
    [ 0.476, 0.71, 0.023 ],
    [ 0.504, 0.72, 0.022 ],
    [ 0.508, 0.71, 0.024 ],
    [ 0.512, 0.72, 0.022 ],
    [ 0.517, 0.71, 0.024 ],
    [ 0.522, 0.72, 0.022 ],
    [ 0.525, 0.72, 0.023 ],
    [ 0.528, 0.71, 0.024 ],
    [ 0.532, 0.72, 0.023 ],
    [ 0.569, 0.72, 0.024 ],
    [ 0.572, 0.72, 0.023 ],
    [ 0.574, 0.72, 0.024 ],
    [ 0.629, 0.72, 0.025 ],
    [ 0.632, 0.72, 0.024 ],
    [ 0.635, 0.72, 0.025 ],
    [ 0.642, 0.72, 0.025 ],
    [ 0.682, 0.73, 0.024 ],
    [ 0.685, 0.72, 0.025 ],
    [ 0.692, 0.73, 0.024 ],
    [ 0.699, 0.72, 0.025 ],
    [ 0.702, 0.73, 0.024 ],
    [ 0.711, 0.73, 0.024 ],
    [ 0.72, 0.72, 0.026 ],
    [ 0.722, 0.73, 0.024 ],
    [ 0.73, 0.72, 0.026 ],
    [ 0.732, 0.73, 0.024 ],
    [ 0.74, 0.72, 0.026 ],
    [ 0.742, 0.73, 0.024 ],
    [ 0.75, 0.72, 0.026 ],
    [ 0.752, 0.73, 0.024 ],
    [ 0.759, 0.73, 0.025 ],
    [ 0.762, 0.73, 0.024 ],
    [ 0.766, 0.73, 0.025 ],
    [ 0.772, 0.73, 0.025 ],
    [ 0.911, 0.73, 0.025 ],
    [ 0.917, 0.73, 0.026 ],
    [ 0.922, 0.73, 0.025 ],
    [ 0.926, 0.73, 0.026 ],
    [ 0.932, 0.73, 0.025 ],
    [ 0.935, 0.73, 0.026 ],
    [ 0.942, 0.73, 0.026 ],
    [ 1.24, 0.73, 0.027 ],
    [ 1.242, 0.73, 0.026 ],
    [ 1.249, 0.73, 0.027 ],
    [ 1.252, 0.73, 0.026 ],
    [ 1.258, 0.73, 0.027 ],
    [ 1.262, 0.73, 0.026 ],
    [ 1.267, 0.73, 0.027 ],
    [ 1.272, 0.73, 0.026 ],
    [ 1.276, 0.73, 0.027 ],
    [ 1.282, 0.74, 0.025 ],
    [ 1.284, 0.73, 0.027 ],
    [ 1.292, 0.74, 0.025 ],
    [ 1.294, 0.73, 0.027 ],
    [ 1.302, 0.74, 0.025 ],
    [ 1.304, 0.73, 0.027 ],
    [ 1.312, 0.74, 0.025 ],
    [ 1.314, 0.73, 0.027 ],
    [ 1.322, 0.74, 0.025 ],
    [ 1.324, 0.73, 0.027 ],
    [ 1.332, 0.74, 0.025 ],
    [ 1.334, 0.73, 0.027 ],
    [ 1.342, 0.73, 0.027 ],
    [ 1.352, 0.73, 0.027 ],
    [ 1.682, 0.73, 0.027 ],
    [ 1.692, 0.73, 0.027 ],
    [ 1.702, 0.74, 0.026 ],
    [ 1.704, 0.73, 0.027 ],
    [ 1.712, 0.73, 0.027 ],
    [ 1.722, 0.74, 0.026 ],
    [ 1.724, 0.73, 0.027 ],
    [ 1.732, 0.74, 0.026 ],
    [ 1.734, 0.73, 0.027 ],
    [ 1.742, 0.74, 0.026 ],
    [ 1.745, 0.73, 0.027 ],
    [ 1.752, 0.74, 0.026 ],
    [ 1.755, 0.73, 0.027 ],
    [ 1.762, 0.74, 0.026 ],
    [ 1.766, 0.73, 0.027 ],
    [ 1.772, 0.74, 0.026 ],
    [ 1.777, 0.73, 0.027 ],
    [ 1.782, 0.74, 0.026 ],
    [ 1.787, 0.73, 0.027 ],
    [ 1.792, 0.74, 0.026 ],
    [ 1.798, 0.73, 0.027 ],
    [ 1.802, 0.74, 0.026 ],
    [ 1.808, 0.73, 0.027 ],
    [ 1.812, 0.74, 0.026 ],
    [ 1.819, 0.73, 0.027 ],
    [ 1.822, 0.74, 0.026 ],
    [ 1.829, 0.73, 0.027 ],
    [ 1.832, 0.74, 0.026 ],
    [ 1.84, 0.73, 0.027 ],
    [ 1.842, 0.74, 0.026 ],
    [ 1.851, 0.74, 0.026 ],
    [ 1.861, 0.74, 0.026 ],
    [ 1.871, 0.74, 0.026 ],
    [ 1.881, 0.74, 0.026 ]
  ],
  "0.375": [
    [ 0.376, 0.69, 0.02 ],
    [ 0.379, 0.69, 0.021 ],
    [ 0.391, 0.69, 0.021 ],
    [ 0.393, 0.69, 0.022 ],
    [ 0.401, 0.7, 0.021 ],
    [ 0.403, 0.69, 0.022 ],
    [ 0.406, 0.69, 0.023 ],
    [ 0.412, 0.7, 0.021 ],
    [ 0.415, 0.7, 0.022 ],
    [ 0.42, 0.69, 0.024 ],
    [ 0.422, 0.7, 0.022 ],
    [ 0.43, 0.7, 0.023 ],
    [ 0.432, 0.7, 0.022 ],
    [ 0.434, 0.7, 0.023 ],
    [ 0.449, 0.7, 0.024 ],
    [ 0.452, 0.7, 0.024 ],
    [ 0.469, 0.7, 0.025 ],
    [ 0.472, 0.71, 0.023 ],
    [ 0.478, 0.7, 0.025 ],
    [ 0.482, 0.71, 0.023 ],
    [ 0.486, 0.71, 0.024 ],
    [ 0.492, 0.71, 0.024 ],
    [ 0.51, 0.71, 0.025 ],
    [ 0.512, 0.71, 0.024 ],
    [ 0.514, 0.71, 0.025 ],
    [ 0.54, 0.71, 0.026 ],
    [ 0.542, 0.71, 0.025 ],
    [ 0.545, 0.71, 0.026 ],
    [ 0.552, 0.71, 0.026 ],
    [ 0.562, 0.71, 0.026 ],
    [ 0.572, 0.72, 0.025 ],
    [ 0.58, 0.71, 0.027 ],
    [ 0.582, 0.72, 0.025 ],
    [ 0.59, 0.71, 0.027 ],
    [ 0.592, 0.72, 0.025 ],
    [ 0.601, 0.72, 0.025 ],
    [ 0.606, 0.72, 0.026 ],
    [ 0.651, 0.72, 0.026 ],
    [ 0.657, 0.72, 0.027 ],
    [ 0.662, 0.72, 0.027 ],
    [ 0.72, 0.72, 0.028 ],
    [ 0.722, 0.72, 0.027 ],
    [ 0.726, 0.72, 0.028 ],
    [ 0.732, 0.73, 0.026 ],
    [ 0.735, 0.72, 0.028 ],
    [ 0.742, 0.73, 0.026 ],
    [ 0.744, 0.72, 0.028 ],
    [ 0.752, 0.73, 0.026 ],
    [ 0.754, 0.72, 0.028 ],
    [ 0.762, 0.73, 0.026 ],
    [ 0.764, 0.72, 0.028 ],
    [ 0.772, 0.73, 0.026 ],
    [ 0.774, 0.72, 0.028 ],
    [ 0.782, 0.73, 0.027 ],
    [ 0.79, 0.72, 0.028 ],
    [ 0.792, 0.73, 0.027 ],
    [ 0.879, 0.73, 0.028 ],
    [ 0.882, 0.73, 0.027 ],
    [ 0.886, 0.73, 0.028 ],
    [ 0.892, 0.73, 0.027 ],
    [ 0.895, 0.73, 0.028 ],
    [ 1.061, 0.73, 0.028 ],
    [ 1.068, 0.73, 0.029 ],
    [ 1.072, 0.73, 0.028 ],
    [ 1.076, 0.73, 0.029 ],
    [ 1.082, 0.73, 0.028 ],
    [ 1.084, 0.73, 0.029 ],
    [ 1.092, 0.73, 0.029 ],
    [ 1.462, 0.74, 0.028 ],
    [ 1.464, 0.73, 0.029 ],
    [ 1.472, 0.74, 0.028 ],
    [ 1.475, 0.73, 0.029 ],
    [ 1.482, 0.74, 0.028 ],
    [ 1.486, 0.73, 0.029 ],
    [ 1.492, 0.74, 0.028 ],
    [ 1.497, 0.73, 0.029 ],
    [ 1.502, 0.74, 0.028 ],
    [ 1.508, 0.73, 0.029 ],
    [ 1.512, 0.74, 0.028 ],
    [ 1.519, 0.73, 0.029 ],
    [ 1.522, 0.74, 0.028 ],
    [ 1.531, 0.74, 0.028 ],
    [ 1.871, 0.74, 0.028 ],
    [ 1.88, 0.74, 0.029 ],
    [ 1.882, 0.74, 0.028 ],
    [ 1.889, 0.74, 0.029 ],
    [ 1.892, 0.74, 0.028 ],
    [ 1.899, 0.74, 0.029 ],
    [ 1.902, 0.74, 0.028 ],
    [ 1.909, 0.74, 0.029 ],
    [ 1.912, 0.74, 0.028 ],
    [ 1.918, 0.74, 0.029 ],
    [ 1.922, 0.74, 0.028 ],
    [ 1.928, 0.74, 0.029 ],
    [ 1.932, 0.74, 0.028 ],
    [ 1.936, 0.74, 0.029 ],
    [ 1.942, 0.74, 0.028 ],
    [ 1.946, 0.74, 0.029 ],
    [ 1.952, 0.74, 0.028 ],
    [ 1.955, 0.74, 0.029 ],
    [ 1.962, 0.74, 0.028 ],
    [ 1.965, 0.74, 0.029 ],
    [ 1.972, 0.74, 0.028 ],
    [ 1.975, 0.74, 0.029 ],
    [ 1.982, 0.74, 0.028 ],
    [ 1.984, 0.74, 0.029 ],
    [ 1.992, 0.74, 0.029 ]
  ],
  "0.408": [
    [ 0.409, 0.69, 0.022 ],
    [ 0.414, 0.69, 0.023 ],
    [ 0.427, 0.69, 0.024 ],
    [ 0.44, 0.69, 0.025 ],
    [ 0.442, 0.7, 0.023 ],
    [ 0.446, 0.69, 0.025 ],
    [ 0.452, 0.7, 0.023 ],
    [ 0.456, 0.7, 0.024 ],
    [ 0.469, 0.7, 0.025 ],
    [ 0.487, 0.7, 0.026 ],
    [ 0.492, 0.7, 0.026 ],
    [ 0.508, 0.7, 0.027 ],
    [ 0.512, 0.71, 0.025 ],
    [ 0.517, 0.7, 0.027 ],
    [ 0.522, 0.71, 0.025 ],
    [ 0.527, 0.71, 0.026 ],
    [ 0.551, 0.71, 0.026 ],
    [ 0.555, 0.71, 0.027 ],
    [ 0.58, 0.71, 0.028 ],
    [ 0.582, 0.71, 0.027 ],
    [ 0.584, 0.71, 0.028 ],
    [ 0.611, 0.72, 0.026 ],
    [ 0.614, 0.71, 0.028 ],
    [ 0.617, 0.71, 0.029 ],
    [ 0.622, 0.72, 0.027 ],
    [ 0.628, 0.71, 0.029 ],
    [ 0.632, 0.72, 0.027 ],
    [ 0.64, 0.71, 0.029 ],
    [ 0.642, 0.72, 0.027 ],
    [ 0.647, 0.72, 0.028 ],
    [ 0.652, 0.72, 0.028 ],
    [ 0.691, 0.72, 0.028 ],
    [ 0.697, 0.72, 0.029 ],
    [ 0.702, 0.72, 0.029 ],
    [ 0.782, 0.73, 0.028 ],
    [ 0.8, 0.73, 0.029 ],
    [ 0.802, 0.73, 0.028 ],
    [ 0.807, 0.73, 0.029 ],
    [ 0.812, 0.73, 0.028 ],
    [ 0.815, 0.73, 0.029 ],
    [ 1.046, 0.74, 0.029 ],
    [ 2.001, 0.75, 0.029 ]
  ],
  "0.416": [
    [ 0.417, 0.69, 0.023 ],
    [ 0.427, 0.69, 0.024 ],
    [ 0.432, 0.69, 0.024 ],
    [ 0.44, 0.69, 0.025 ],
    [ 0.442, 0.69, 0.024 ],
    [ 0.444, 0.69, 0.025 ],
    [ 0.452, 0.7, 0.023 ],
    [ 0.456, 0.69, 0.026 ],
    [ 0.462, 0.7, 0.024 ],
    [ 0.47, 0.7, 0.025 ],
    [ 0.487, 0.7, 0.026 ],
    [ 0.492, 0.7, 0.026 ],
    [ 0.508, 0.7, 0.027 ],
    [ 0.512, 0.71, 0.025 ],
    [ 0.514, 0.7, 0.027 ],
    [ 0.522, 0.71, 0.025 ],
    [ 0.526, 0.71, 0.026 ],
    [ 0.53, 0.7, 0.028 ],
    [ 0.532, 0.71, 0.026 ],
    [ 0.541, 0.71, 0.026 ],
    [ 0.55, 0.71, 0.027 ],
    [ 0.552, 0.71, 0.027 ],
    [ 0.578, 0.71, 0.028 ],
    [ 0.608, 0.71, 0.029 ],
    [ 0.612, 0.71, 0.029 ],
    [ 0.622, 0.72, 0.027 ],
    [ 0.624, 0.71, 0.029 ],
    [ 0.632, 0.72, 0.027 ],
    [ 0.636, 0.71, 0.029 ],
    [ 0.642, 0.72, 0.028 ],
    [ 0.679, 0.72, 0.029 ],
    [ 0.682, 0.72, 0.028 ],
    [ 0.684, 0.72, 0.029 ],
    [ 0.772, 0.73, 0.028 ],
    [ 0.778, 0.73, 0.029 ],
    [ 0.782, 0.73, 0.028 ],
    [ 0.786, 0.73, 0.029 ],
    [ 0.997, 0.74, 0.029 ],
    [ 1.654, 0.74, 0.029 ],
    [ 1.662, 0.75, 0.029 ]
  ],
  "0.510": [
    [ 0.511, 0.69, 0.028 ],
    [ 0.521, 0.69, 0.028 ],
    [ 0.523, 0.69, 0.029 ],
    [ 0.544, 0.7, 0.028 ],
    [ 0.551, 0.7, 0.028 ],
    [ 0.554, 0.7, 0.029 ],
    [ 0.592, 0.71, 0.029 ],
    [ 0.648, 0.72, 0.029 ],
    [ 0.726, 0.73, 0.029 ],
    [ 0.833, 0.74, 0.029 ],
    [ 1.006, 0.75, 0.029 ],
    [ 1.349, 0.76, 0.029 ],
    [ 2.87, 0.76, 0.029 ],
    [ 2.879, 0.77, 0.029 ],
    [ 2.885, 0.76, 0.029 ],
    [ 2.889, 0.77, 0.029 ],
    [ 2.897, 0.77, 0.029 ]
  ],
  "1.000": [
    [ 1.001, 0.72, 0.029 ],
    [ 1.05, 0.73, 0.029 ],
    [ 1.137, 0.74, 0.029 ],
    [ 1.246, 0.75, 0.029 ],
    [ 1.388, 0.76, 0.029 ],
    [ 1.586, 0.77, 0.029 ],
    [ 1.884, 0.78, 0.029 ],
    [ 2.426, 0.79, 0.029 ]
  ]
}
